// src/components/MessageList.jsx
import React, { useCallback, useRef, useEffect, useLayoutEffect, useState } from "react";
import { getDiscordAvatarUrl } from "../func/utils";

function UserMentionTooltip({ user, isOpen, onClose, position }) {
    const tooltipRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;

        function handleClickOutside(event) {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                onClose();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!user || !isOpen || !position) return null;

    return (
        <div 
            ref={tooltipRef}
            className="fixed bg-slate-800 text-white rounded-lg shadow-lg p-3 z-50"
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                transform: 'translateY(-100%) translateX(-50%)',
                marginBottom: '8px'
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center gap-3">
                <img
                    src={user.avatar || "https://discord.com/assets/ca24969f2fd7a9fb03d5.png"}
                    alt=""
                    className="w-10 h-10 rounded-full"
                    onError={(e) => {
                        if (!e.currentTarget.src.includes("discord.com/assets")) {
                            e.currentTarget.src = "https://discord.com/assets/ca24969f2fd7a9fb03d5.png";
                        }
                    }}
                />
                <div>
                    <div className="font-medium">{user.username}</div>
                    <div className="text-xs text-slate-400">
                        <span className="select-none">ID: </span>
                        <span className="select-text cursor-text">{user.userId}</span>
                    </div>
                </div>
            </div>
            {/* Arrow */}
            <div className="absolute -bottom-2 left-1/2 w-4 h-4 bg-slate-800 transform -translate-x-1/2 rotate-45"></div>
        </div>
    );
}

function parseMessageContent(content, mentions = [], handleMentionClick) {
    if (!content) return [];

    const parts = [];
    let lastIndex = 0;

    // Create a map of userId to user data for quick lookup, filtering out null mentions
    const mentionMap = new Map(
        mentions
            .filter(m => m && m.user)
            .map(m => [m.user.userId, m.user])
    );

    // Combined regex for emojis and mentions
    const regex = /<(?:(?:a)?:([^:]+):(\d+)|@(\d+))>/g;
    let match;

    while ((match = regex.exec(content)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            parts.push(content.substring(lastIndex, match.index));
        }

        // Check if this is an emoji (group 1 and 2) or mention (group 3)
        if (match[1] && match[2]) {
            // This is an emoji
            const [, name, id] = match;
            const emojiUrl = `https://cdn.discordapp.com/emojis/${id}.webp?size=44${match[0].startsWith('<a:') ? '&animated=true' : ''}`;
            parts.push(
                <img 
                    key={`${name}-${id}-${match.index}`}
                    className="inline-block h-6 w-6 align-middle"
                    src={emojiUrl}
                    alt={name}
                />
            );
        } else if (match[3]) {
            // This is a mention
            const userId = match[3];
            const user = mentionMap.get(userId);
            
            if (user) {
                const tooltipId = `mention-${userId}-${match.index}`;
                parts.push(
                    <span 
                        key={tooltipId}
                        className="relative inline-flex items-center bg-indigo-500/10 text-indigo-500 dark:text-indigo-400 rounded px-1.5 py-0.5 cursor-pointer"
                        onClick={(e) => handleMentionClick(e, tooltipId, user)}
                    >
                        @{user.username}
                    </span>
                );
            } else {
                // Unresolved mention - show raw format but styled
                parts.push(
                    <span 
                        key={`mention-unknown-${userId}-${match.index}`}
                        className="bg-indigo-500/10 text-indigo-500 dark:text-indigo-400 rounded px-1.5 py-0.5"
                    >
                        {match[0]}
                    </span>
                );
            }
        }

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
        parts.push(content.substring(lastIndex));
    }

    return parts;
}

function ReplyTooltip({ reference, isOpen, onClose }) {
    const tooltipRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;

        function handleClickOutside(event) {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                onClose();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!reference || !isOpen) return null;

    return (
        <div 
            ref={tooltipRef}
            className="absolute bottom-full left-0 mb-2 w-max max-w-sm bg-slate-800 text-white rounded-lg shadow-lg p-3 z-10
                      pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center gap-2 mb-1">
                <img
                    src={reference.author.avatar || "https://discord.com/assets/ca24969f2fd7a9fb03d5.png"}
                    alt=""
                    className="w-6 h-6 rounded-full"
                    onError={(e) => {
                        if (!e.currentTarget.src.includes("discord.com/assets")) {
                            e.currentTarget.src = "https://discord.com/assets/ca24969f2fd7a9fb03d5.png";
                        }
                    }}
                />
                <div>
                    <div className="font-medium">{reference.author.username}</div>
                    <div className="text-xs text-slate-400">
                        <span className="select-none">ID: </span>
                        <span className="select-text cursor-text">{reference.authorId}</span>
                    </div>
                </div>
            </div>
            <p className="text-sm text-slate-200 break-words">
                {reference.content}
            </p>
            {/* Arrow */}
            <div className="absolute -bottom-2 left-4 w-4 h-4 bg-slate-800 transform rotate-45"></div>
        </div>
    );
}

function EditHistoryPopup({ editHistory, onClose }) {
    if (!editHistory || editHistory.length === 0) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white">Edit History</h3>
                    <button 
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-500 dark:hover:text-slate-300"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="overflow-y-auto max-h-[60vh] p-4 space-y-4">
                    {editHistory.map((edit, index) => (
                        <div key={edit.timestamp} className="border-b border-slate-200 dark:border-slate-700 last:border-0 pb-4 last:pb-0">
                            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                                {new Date(edit.timestamp * 1000).toLocaleString()}
                            </div>
                            <p 
                                className="text-slate-600 dark:text-slate-300 break-words"
                                dangerouslySetInnerHTML={{ __html: parseMessageContent(edit.content, edit.mentions) }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ContextModal({
    messages,
    anchorMessageId,
    isOpen,
    onClose,
    loading,
    loadingOlder,
    loadingNewer,
    hasOlder,
    hasNewer,
    onViewContext,
    onFetchOlder,
    onFetchNewer,
}) {
    const scrollContainerRef = useRef(null);
    const messageRefs = useRef({});
    const scrolledAnchorRef = useRef(null);
    const olderFetchRef = useRef(false);
    const newerFetchRef = useRef(false);
    const edgeLoadingReadyRef = useRef(false);
    const suppressProgrammaticScrollRef = useRef(false);
    const anchorScrollReleaseTimerRef = useRef(null);
    const prependScrollHeightRef = useRef(null);
    const [selectedEditHistory, setSelectedEditHistory] = useState(null);
    const [openReplyTooltip, setOpenReplyTooltip] = useState(null);

    useEffect(() => {
        return () => {
            if (anchorScrollReleaseTimerRef.current) {
                clearTimeout(anchorScrollReleaseTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            scrolledAnchorRef.current = null;
            edgeLoadingReadyRef.current = false;
            suppressProgrammaticScrollRef.current = false;
            if (anchorScrollReleaseTimerRef.current) {
                clearTimeout(anchorScrollReleaseTimerRef.current);
                anchorScrollReleaseTimerRef.current = null;
            }
            return;
        }

        if (scrolledAnchorRef.current !== anchorMessageId) {
            scrolledAnchorRef.current = null;
            edgeLoadingReadyRef.current = false;
            suppressProgrammaticScrollRef.current = true;
            if (anchorScrollReleaseTimerRef.current) {
                clearTimeout(anchorScrollReleaseTimerRef.current);
                anchorScrollReleaseTimerRef.current = null;
            }
        }
    }, [anchorMessageId, isOpen]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || prependScrollHeightRef.current === null) return;

        const previousScrollHeight = prependScrollHeightRef.current;
        prependScrollHeightRef.current = null;
        container.scrollTop += container.scrollHeight - previousScrollHeight;
    }, [messages.length]);

    useLayoutEffect(() => {
        if (!isOpen || loading || !anchorMessageId || scrolledAnchorRef.current === anchorMessageId) return;

        const container = scrollContainerRef.current;
        const anchorElement = messageRefs.current[anchorMessageId];
        if (!container || !anchorElement) {
            edgeLoadingReadyRef.current = true;
            suppressProgrammaticScrollRef.current = false;
            return;
        }

        edgeLoadingReadyRef.current = false;
        suppressProgrammaticScrollRef.current = true;

        const containerRect = container.getBoundingClientRect();
        const anchorRect = anchorElement.getBoundingClientRect();
        const anchorOffset = anchorRect.top - containerRect.top + container.scrollTop;
        container.scrollTop = Math.max(
            0,
            anchorOffset - (container.clientHeight / 2) + (anchorElement.offsetHeight / 2)
        );
        scrolledAnchorRef.current = anchorMessageId;

        if (anchorScrollReleaseTimerRef.current) {
            clearTimeout(anchorScrollReleaseTimerRef.current);
        }
        anchorScrollReleaseTimerRef.current = setTimeout(() => {
            edgeLoadingReadyRef.current = true;
            suppressProgrammaticScrollRef.current = false;
            anchorScrollReleaseTimerRef.current = null;
        }, 120);
    }, [anchorMessageId, isOpen, loading, messages.length]);

    const fetchOlderFromContextEdge = useCallback((container) => {
        if (!container || loading || !hasOlder || loadingOlder || olderFetchRef.current) return;

        olderFetchRef.current = true;
        prependScrollHeightRef.current = container.scrollHeight;
        Promise.resolve(onFetchOlder()).finally(() => {
            olderFetchRef.current = false;
        });
    }, [hasOlder, loading, loadingOlder, onFetchOlder]);

    const fetchNewerFromContextEdge = useCallback(() => {
        if (loading || !hasNewer || loadingNewer || newerFetchRef.current) return;

        newerFetchRef.current = true;
        Promise.resolve(onFetchNewer()).finally(() => {
            newerFetchRef.current = false;
        });
    }, [hasNewer, loading, loadingNewer, onFetchNewer]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!isOpen || !container || loading || messages.length === 0 || !edgeLoadingReadyRef.current) return;

        const { scrollTop, clientHeight, scrollHeight } = container;
        if (scrollTop <= 140) {
            fetchOlderFromContextEdge(container);
        }
        if (scrollTop + clientHeight >= scrollHeight - 140) {
            fetchNewerFromContextEdge();
        }
    }, [fetchNewerFromContextEdge, fetchOlderFromContextEdge, hasNewer, hasOlder, isOpen, loading, loadingNewer, loadingOlder, messages.length]);

    function handleContextScroll() {
        const container = scrollContainerRef.current;
        if (!container || loading || !edgeLoadingReadyRef.current || suppressProgrammaticScrollRef.current) return;

        setOpenReplyTooltip(null);
        const { scrollTop, clientHeight, scrollHeight } = container;

        if (scrollTop <= 140 && hasOlder && !loadingOlder && !olderFetchRef.current) {
            fetchOlderFromContextEdge(container);
        }

        if (scrollTop + clientHeight >= scrollHeight - 140 && hasNewer && !loadingNewer && !newerFetchRef.current) {
            fetchNewerFromContextEdge();
        }
    }

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
            <div 
                className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden" 
                onClick={e => e.stopPropagation()}
            >
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white">Message Context</h3>
                    <button 
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-500 dark:hover:text-slate-300"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div
                    ref={scrollContainerRef}
                    onScroll={handleContextScroll}
                    className="overflow-y-auto max-h-[60vh]"
                >
                    {loading ? (
                        <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                        </div>
                    ) : (
                        <div className="p-4 space-y-4">
                            {loadingOlder && (
                                <div className="flex justify-center py-2">
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-indigo-500"></div>
                                </div>
                            )}
                            {!hasOlder && messages.length > 0 && (
                                <div className="text-center text-xs font-medium text-slate-400 dark:text-slate-500">
                                    No older messages in this channel
                                </div>
                            )}
                            {messages.map((msg) => (
                                <div
                                    key={msg.messageId}
                                    ref={(element) => {
                                        if (element) {
                                            messageRefs.current[msg.messageId] = element;
                                        } else {
                                            delete messageRefs.current[msg.messageId];
                                        }
                                    }}
                                    className={`flex gap-4 p-4 rounded-lg transition duration-200 ${
                                        msg.messageId === anchorMessageId
                                            ? "bg-indigo-50 ring-1 ring-indigo-200 dark:bg-indigo-950/30 dark:ring-indigo-800"
                                            : "hover:bg-slate-50 dark:hover:bg-slate-700/50"
                                    }`}
                                >
                                    {/* Avatar */}
                                    <img
                                        src={getDiscordAvatarUrl(msg.author)}
                                        alt=""
                                        className="w-10 h-10 rounded-full object-cover flex-shrink-0 
                                                 ring-2 ring-white dark:ring-slate-700 group-hover:ring-indigo-500 dark:group-hover:ring-indigo-400
                                                 transition duration-200"
                                        onError={(e) => {
                                            if (!e.currentTarget.src.includes("cdn.discordapp.com/embed/avatars/")) {
                                                e.currentTarget.src = "https://cdn.discordapp.com/embed/avatars/0.png";
                                            }
                                        }}
                                    />

                                    {/* Message Content */}
                                    <div className="flex-1 min-w-0">
                                        {/* Header */}
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                                            <span className="font-medium text-slate-900 dark:text-white">
                                                {msg.author?.username || "Unknown User"}
                                            </span>
                                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                                <span>
                                                    {new Date(msg.createdAt * 1000).toLocaleTimeString([], {
                                                        year: "numeric",
                                                        month: "short",
                                                        day: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </span>
                                                <span className="text-slate-400 dark:text-slate-500">
                                                    User ID: {msg.author?.userId || msg.authorId}
                                                </span>
                                                {msg.editHistory && msg.editHistory.length > 0 && (
                                                    <button
                                                        onClick={() => setSelectedEditHistory(msg.editHistory)}
                                                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                                    >
                                                        (edited)
                                                    </button>
                                                )}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onViewContext(msg.messageId);
                                                    }}
                                                    className="text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400"
                                                >
                                                    View Context
                                                </button>
                                            </div>
                                        </div>

                                        {/* Reply Indicator */}
                                        {msg.reference && (
                                            <div className="relative mb-1">
                                                <div 
                                                    className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 w-fit cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenReplyTooltip(openReplyTooltip === msg.messageId ? null : msg.messageId);
                                                    }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                                    </svg>
                                                    <span>Replying to {msg.reference.author?.username || "Unknown"}</span>
                                                    <ReplyTooltip 
                                                        reference={msg.reference} 
                                                        isOpen={openReplyTooltip === msg.messageId}
                                                        onClose={() => setOpenReplyTooltip(null)}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Message */}
                                        <p className="text-slate-600 dark:text-slate-300 break-words">
                                            {parseMessageContent(msg.content, msg.mentions)}
                                        </p>

                                        {/* Attachments */}
                                        {msg.attachments && msg.attachments.length > 0 && (
                                            <div className="mt-2 space-y-1">
                                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                                    {msg.attachments.length} attachment{msg.attachments.length !== 1 ? 's' : ''}:
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {msg.attachments.map((attachment, index) => (
                                                        <a
                                                            key={index}
                                                            href={attachment.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-sm text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 
                                                                     bg-indigo-50 dark:bg-indigo-900/20 px-2 py-1 rounded-md 
                                                                     hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                                                        >
                                                            {attachment.filename}
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Channel */}
                                        <div className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                                            #{msg.channel?.name}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {loadingNewer && (
                                <div className="flex justify-center py-2">
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-indigo-500"></div>
                                </div>
                            )}
                            {!hasNewer && messages.length > 0 && (
                                <div className="text-center text-xs font-medium text-slate-400 dark:text-slate-500">
                                    No newer messages in this channel
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Edit History Popup */}
            {selectedEditHistory && (
                <EditHistoryPopup 
                    editHistory={selectedEditHistory} 
                    onClose={() => setSelectedEditHistory(null)} 
                />
            )}
        </div>
    );
}

export default function MessageList({
    messages,
    loading,
    hasMore,
    fetchMoreMessages,
    contextMessages = [],
    contextAnchorMessageId = null,
    loadingContext = false,
    loadingOlderContext = false,
    loadingNewerContext = false,
    hasOlderContext = true,
    hasNewerContext = true,
    onViewContext = () => {},
    onFetchOlderContext = () => {},
    onFetchNewerContext = () => {},
}) {
    const containerRef = useRef(null);
    const loadMoreRef = useRef(null);
    const [selectedEditHistory, setSelectedEditHistory] = useState(null);
    const [openTooltip, setOpenTooltip] = useState(null);
    const [openReplyTooltip, setOpenReplyTooltip] = useState(null);
    const [isContextModalOpen, setIsContextModalOpen] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        function handleScroll() {
            const { scrollTop, clientHeight, scrollHeight } = container;
            // Close tooltips when scrolling
            setOpenTooltip(null);
            setOpenReplyTooltip(null);
            
            if (!loading && hasMore && scrollTop + clientHeight >= scrollHeight - 160) {
                fetchMoreMessages();
            }
        }

        container.addEventListener("scroll", handleScroll);
        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [fetchMoreMessages, loading, hasMore]);

    useEffect(() => {
        const sentinel = loadMoreRef.current;
        if (!sentinel || !hasMore || loading || messages.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    fetchMoreMessages();
                }
            },
            {
                root: null,
                rootMargin: "240px 0px",
                threshold: 0,
            }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [fetchMoreMessages, hasMore, loading, messages.length]);

    if (messages.length === 0 && !loading) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
                <div className="bg-slate-100 dark:bg-slate-700 rounded-full p-4 mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                </div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                    No Messages Found
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                    Try adjusting your search criteria to find what you're looking for.
                </p>
            </div>
        );
    }

    function handleMentionClick(e, tooltipId, user) {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setOpenTooltip(prev => 
            prev?.id === tooltipId 
                ? null 
                : {
                    id: tooltipId,
                    user,
                    position: {
                        top: rect.top,
                        left: rect.left + (rect.width / 2)
                    }
                }
        );
    }

    return (
        <>
            <div
                ref={containerRef}
                className="h-full overflow-y-auto px-6 py-4 space-y-4"
                onClick={() => {
                    setOpenTooltip(null);
                    setOpenReplyTooltip(null);
                }}
            >
                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className="group flex gap-4 p-4 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition duration-200"
                    >
                        {/* Avatar */}
                        <img
                            src={getDiscordAvatarUrl(msg.author)}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0
                                     ring-2 ring-white dark:ring-slate-700 group-hover:ring-indigo-500 dark:group-hover:ring-indigo-400
                                     transition duration-200"
                            onError={(e) => {
                                if (!e.currentTarget.src.includes("cdn.discordapp.com/embed/avatars/")) {
                                    e.currentTarget.src = "https://cdn.discordapp.com/embed/avatars/0.png";
                                }
                            }}
                        />

                        {/* Message Content */}
                        <div className="flex-1 min-w-0">
                            {/* Header */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                                <span className="font-medium text-slate-900 dark:text-white">
                                    {msg.author.username}
                                </span>
                                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                    <span>
                                        {new Date(msg.createdAt * 1000).toLocaleTimeString([], {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </span>
                                    <span className="text-slate-400 dark:text-slate-500">
                                        User ID: {msg.author.userId}
                                    </span>
                                    {msg.editHistory && msg.editHistory.length > 0 && (
                                        <button
                                            onClick={() => setSelectedEditHistory(msg.editHistory)}
                                            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                        >
                                            (edited)
                                        </button>
                                    )}
                                    <button
                                        onClick={() => {
                                            onViewContext(msg.messageId);
                                            setIsContextModalOpen(true);
                                        }}
                                        className="text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400"
                                    >
                                        View Context
                                    </button>
                                </div>
                            </div>

                            {/* Reply Indicator */}
                            {msg.reference && (
                                <div className="relative mb-1">
                                    <div 
                                        className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 w-fit cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenReplyTooltip(openReplyTooltip === msg.id ? null : msg.id);
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                        </svg>
                                        <span>Replying to {msg.reference.author.username}</span>
                                        <ReplyTooltip 
                                            reference={msg.reference} 
                                            isOpen={openReplyTooltip === msg.id}
                                            onClose={() => setOpenReplyTooltip(null)}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Message */}
                            <p className="text-slate-600 dark:text-slate-300 break-words">
                                {parseMessageContent(msg.content, msg.mentions, handleMentionClick)}
                            </p>

                            {/* Attachments */}
                            {msg.attachments && msg.attachments.length > 0 && (
                                <div className="mt-2 space-y-1">
                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                        {msg.attachments.length} attachment{msg.attachments.length !== 1 ? 's' : ''}:
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {msg.attachments.map((attachment, index) => (
                                            <a
                                                key={index}
                                                href={attachment.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 
                                                         bg-indigo-50 dark:bg-indigo-900/20 px-2 py-1 rounded-md 
                                                         hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                                            >
                                                {attachment.filename}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Channel */}
                            <div className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                                #{msg.channel.name}
                            </div>
                        </div>
                    </div>
                ))}

                {/* Edit History Popup */}
                {selectedEditHistory && (
                    <EditHistoryPopup 
                        editHistory={selectedEditHistory} 
                        onClose={() => setSelectedEditHistory(null)} 
                    />
                )}

                {/* Loading State */}
                {loading && (
                    <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                    </div>
                )}

                {/* End of Messages */}
                {!hasMore && !loading && messages.length > 0 && (
                    <div className="text-center py-4 text-slate-500 dark:text-slate-400">
                        No more messages to load
                    </div>
                )}

                <div ref={loadMoreRef} className="h-px" aria-hidden="true" />
            </div>

            {/* Render tooltip at root level */}
            {openTooltip && (
                <UserMentionTooltip 
                    user={openTooltip.user}
                    isOpen={true}
                    onClose={() => setOpenTooltip(null)}
                    position={openTooltip.position}
                />
            )}

            {/* Context Modal */}
            <ContextModal
                messages={contextMessages}
                anchorMessageId={contextAnchorMessageId}
                isOpen={isContextModalOpen}
                onClose={() => setIsContextModalOpen(false)}
                loading={loadingContext}
                loadingOlder={loadingOlderContext}
                loadingNewer={loadingNewerContext}
                hasOlder={hasOlderContext}
                hasNewer={hasNewerContext}
                onViewContext={onViewContext}
                onFetchOlder={onFetchOlderContext}
                onFetchNewer={onFetchNewerContext}
            />
        </>
    );
}
